import React, { useState } from 'react';
import SafeIcon from './common/SafeIcon';
import Dashboard from './components/Dashboard';
import ShipmentBoard from './components/ShipmentBoard';
import CarrierComparison from './components/CarrierComparison';
import ExceptionsCenter from './components/ExceptionsCenter';
import DemoPage from './components/DemoPage';
import { motion, AnimatePresence } from 'framer-motion';

const SideNav = ({ activeTab, setActiveTab }) => {
  const navItems = [
    { id: 'dashboard', label: 'Control Tower', icon: 'Activity' },
    { id: 'shipments', label: 'Shipment Board', icon: 'Layers' },
    { id: 'carrier', label: 'Carrier Compare', icon: 'Compass' },
    { id: 'exceptions', label: 'Exception Center', icon: 'AlertTriangle', badge: 3 },
    { id: 'demo', label: 'Demo', icon: 'Zap' },
    { id: 'settings', label: 'System Config', icon: 'Settings' },
  ];

  return (
    <div className="w-64 bg-navy-800 border-r border-navy-700 h-screen flex flex-col hidden md:flex shrink-0">
      <div className="p-6 flex items-center gap-3 border-b border-navy-700">
        <div className="w-8 h-8 rounded bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center shadow-[0_0_15px_rgba(255,90,0,0.4)]">
          <SafeIcon name="FastForward" className="text-white text-lg" />
        </div>
        <span className="text-xl font-bold tracking-tight text-white">Freight<span className="text-orange-500">Flow</span></span>
      </div>

      <nav className="flex-grow py-6 px-4 space-y-1">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-200 ${
              activeTab === item.id 
                ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/30 shadow-[0_0_10px_rgba(100,255,218,0.1)]' 
                : 'text-slate-400 hover:bg-navy-700 hover:text-slate-200 border border-transparent'
            }`}
          >
            <div className="flex items-center gap-3">
              <SafeIcon name={item.icon} className={activeTab === item.id ? 'text-cyan-400' : 'text-slate-500'} />
              <span className="font-medium text-sm">{item.label}</span>
            </div>
            {item.badge && (
              <span className="bg-alert-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full">
                {item.badge}
              </span>
            )}
          </button>
        ))}
      </nav>

      <div className="p-4 border-t border-navy-700">
        <div className="bg-navy-900 rounded-lg p-3 border border-navy-600 flex items-center gap-3">
          <img src="https://i.pravatar.cc/150?img=11" alt="User" className="w-8 h-8 rounded-full border border-navy-500" />
          <div>
            <p className="text-xs font-semibold text-slate-200">Alex Coordinator</p>
            <p className="text-[10px] text-slate-500 font-mono">ID: FF-9921</p>
          </div>
        </div>
        <div className="mt-4 text-center">
          <p className="text-[10px] text-slate-600 font-mono tracking-wider">
            Made by LingLink AI and DigLab AI
          </p>
        </div>
      </div>
    </div>
  );
};

const Topbar = () => (
  <header className="h-16 bg-navy-800/80 backdrop-blur-md border-b border-navy-700 flex items-center justify-between px-6 sticky top-0 z-30">
    <div className="flex items-center w-96 bg-navy-900 border border-navy-600 rounded-lg px-3 py-1.5 focus-within:border-cyan-500 focus-within:ring-1 focus-within:ring-cyan-500 transition-all">
      <SafeIcon name="Search" className="text-slate-500 mr-2" />
      <input 
        type="text" 
        placeholder="Search tracking ID, vessel, or port..." 
        className="bg-transparent border-none outline-none text-sm text-slate-200 w-full placeholder-slate-600 font-mono"
      />
      <div className="bg-navy-700 text-slate-400 text-[10px] px-1.5 py-0.5 rounded ml-2">⌘K</div>
    </div>

    <div className="flex items-center gap-4">
      <button className="text-slate-400 hover:text-cyan-400 transition-colors relative">
        <SafeIcon name="Bell" />
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-alert-500 rounded-full animate-ping"></span>
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-alert-500 rounded-full"></span>
      </button>
      <button className="text-slate-400 hover:text-cyan-400 transition-colors">
        <SafeIcon name="MessageSquare" />
      </button>
      <div className="h-6 w-px bg-navy-600 mx-2"></div>
      <button className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors bg-navy-900 px-3 py-1.5 rounded-lg border border-navy-600">
        <SafeIcon name="LogOut" className="text-xs" />
        Exit Terminal
      </button>
    </div>
  </header>
);

function App() {
  const [activeTab, setActiveTab] = useState('dashboard');

  const renderContent = () => {
    switch(activeTab) {
      case 'dashboard': return <Dashboard />;
      case 'shipments': return <ShipmentBoard />;
      case 'carrier': return <CarrierComparison />;
      case 'exceptions': return <ExceptionsCenter />;
      case 'demo': return <DemoPage />;
      case 'settings': return (
        <div className="flex items-center justify-center h-full">
          <div className="text-center space-y-4">
             <SafeIcon name="Settings" className="text-4xl text-slate-600 mx-auto animate-[spin_10s_linear_infinite]" />
             <h2 className="text-xl text-slate-400 font-mono">System Configuration Locked</h2>
             <p className="text-sm text-slate-500">Requires Admin Level 4 clearance.</p>
          </div>
        </div>
      );
      default: return <Dashboard />;
    }
  };

  return (
    <div className="flex h-screen bg-[#040B16] text-slate-300 overflow-hidden bg-grid-pattern">
      <SideNav activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <main className="flex-1 flex flex-col h-screen relative">
        {/* Subtle ambient glow top right */}
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-cyan-900/20 rounded-full blur-[120px] pointer-events-none"></div>
        
        <Topbar />
        
        <div className="flex-1 overflow-y-auto p-4 md:p-8 custom-scrollbar relative z-10">
          <div className="max-w-7xl mx-auto h-full">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTab}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.2 }}
                className="h-full"
              >
                {renderContent()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </main>
    </div>
  );
}

export default App;